import React, { useState, useEffect } from "react";
import "./AccountModal.css";
import { Modal, Button, Input, Alert } from "../ui";
import { useAuth } from "../Context/AuthContext";

type AccountView = "login" | "register" | "forgot" | "profile";

interface AccountModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const AccountModal: React.FC<AccountModalProps> = ({ isOpen, onClose }) => {
  const { user, login, loginWithGoogle, register, logout, updateProfile } = useAuth();
  const [view, setView] = useState<AccountView>("login");

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Profile form
  const [profile, setProfile] = useState({
    name: "", prenom: "", telephone: "",
    adresse: "", complement: "", code_postal: "", ville: "",
  });

  useEffect(() => {
    if (user) {
      setView("profile");
      setProfile({
        name: user.name ?? "",
        prenom: user.prenom ?? "",
        telephone: user.telephone ?? "",
        adresse: user.adresse ?? "",
        complement: user.complement ?? "",
        code_postal: user.code_postal ?? "",
        ville: user.ville ?? "",
      });
    } else {
      setView("login");
    }
  }, [user, isOpen]);

  // Google button
  useEffect(() => {
    if (!isOpen || user || view !== "login") return;
    const w = window as any;
    const tryInit = () => {
      if (w.google?.accounts?.id) {
        w.google.accounts.id.initialize({
          client_id: "552973092045-0p4o56pigs2770mjtot5bmf93nkc2uvt.apps.googleusercontent.com",
          callback: handleGoogleResponse,
        });
        const btn = document.getElementById("google-btn-account");
        if (btn) {
          w.google.accounts.id.renderButton(btn, {
            theme: "outline", size: "large", width: "100%",
          });
        }
      }
    };
    const timer = setTimeout(tryInit, 100);
    return () => clearTimeout(timer);
  }, [isOpen, view, user]);

  const reset = () => { setError(null); setSuccess(null); setEmail(""); setPassword(""); setName(""); };

  const handleGoogleResponse = async (resp: any) => {
    setLoading(true); setError(null);
    const r = await loginWithGoogle(resp.credential);
    if (!r.ok) setError(r.error ?? "Connexion Google échouée.");
    setLoading(false);
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault(); setLoading(true); setError(null);
    const r = await login(email, password);
    if (!r.ok) setError(r.error ?? "Identifiants incorrects.");
    setLoading(false);
  };

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault(); setLoading(true); setError(null);
    const r = await register(email, password, name);
    if (!r.ok) setError(r.error ?? "Erreur lors de la création du compte.");
    setLoading(false);
  };

  const handleForgot = async (e: React.FormEvent) => {
    e.preventDefault(); setLoading(true); setError(null);
    try {
      const res = await fetch("/backend/api/forgot-password.php", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });
      const data = await res.json();
      if (data.success) setSuccess("Un lien de réinitialisation vous a été envoyé.");
      else setError(data.error || "Erreur lors de la demande.");
    } catch { setError("Erreur réseau."); }
    setLoading(false);
  };

  const handleProfileSave = async (e: React.FormEvent) => {
    e.preventDefault(); setLoading(true); setError(null); setSuccess(null);
    const r = await updateProfile(profile);
    if (r.ok) setSuccess("Profil mis à jour !");
    else setError(r.error ?? "Erreur lors de la mise à jour.");
    setLoading(false);
  };

  const handleLogout = async () => {
    await logout();
    onClose();
  };

  const title = user
    ? "Mon compte"
    : view === "login" ? "Connexion"
    : view === "register" ? "Créer un compte"
    : "Mot de passe oublié";

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={title} size="sm">
      {error   && <Alert type="error">{error}</Alert>}
      {success && <Alert type="success">{success}</Alert>}

      {/* ── Profil connecté ── */}
      {user && view === "profile" && (
        <form className="account-form" onSubmit={handleProfileSave}>
          <p className="account-email">
            Connecté en tant que <strong>{user.email}</strong>
          </p>
          <div className="account-form__grid">
            <Input label="Nom" value={profile.name} onChange={(e) => setProfile({ ...profile, name: e.target.value })} />
            <Input label="Prénom" value={profile.prenom} onChange={(e) => setProfile({ ...profile, prenom: e.target.value })} />
            <Input label="Téléphone" value={profile.telephone} onChange={(e) => setProfile({ ...profile, telephone: e.target.value })} />
            <Input label="Code postal" value={profile.code_postal} onChange={(e) => setProfile({ ...profile, code_postal: e.target.value })} />
          </div>
          <Input label="Adresse" value={profile.adresse} onChange={(e) => setProfile({ ...profile, adresse: e.target.value })} />
          <Input label="Complément" value={profile.complement} onChange={(e) => setProfile({ ...profile, complement: e.target.value })} />
          <Input label="Ville" value={profile.ville} onChange={(e) => setProfile({ ...profile, ville: e.target.value })} />

          <div className="account-form__actions">
            <Button type="submit" variant="primary" loading={loading} fullWidth>
              Enregistrer
            </Button>
            <Button variant="danger" fullWidth onClick={handleLogout} type="button">
              Se déconnecter
            </Button>
          </div>
        </form>
      )}

      {/* ── Login ── */}
      {!user && view === "login" && (
        <form className="account-form" onSubmit={handleLogin}>
          <Input label="Email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
          <Input label="Mot de passe" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required />
          <Button type="submit" variant="primary" fullWidth loading={loading}>Se connecter</Button>
          <div id="google-btn-account" />
          <div className="account-links">
            <button type="button" className="account-link" onClick={() => { reset(); setView("register"); }}>
              Créer un compte
            </button>
            <button type="button" className="account-link" onClick={() => { reset(); setView("forgot"); }}>
              Mot de passe oublié
            </button>
          </div>
        </form>
      )}

      {/* ── Register ── */}
      {!user && view === "register" && (
        <form className="account-form" onSubmit={handleRegister}>
          <Input label="Nom" value={name} onChange={(e) => setName(e.target.value)} required />
          <Input label="Email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
          <Input label="Mot de passe (6 caractères min)" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={6} />
          <Button type="submit" variant="primary" fullWidth loading={loading}>Créer mon compte</Button>
          <button type="button" className="account-link" onClick={() => { reset(); setView("login"); }}>
            ← Retour à la connexion
          </button>
        </form>
      )}

      {/* ── Forgot ── */}
      {!user && view === "forgot" && (
        <form className="account-form" onSubmit={handleForgot}>
          <Input label="Email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
          <Button type="submit" variant="primary" fullWidth loading={loading}>Envoyer le lien</Button>
          <button type="button" className="account-link" onClick={() => { reset(); setView("login"); }}>
            ← Retour à la connexion
          </button>
        </form>
      )}
    </Modal>
  );
};

export default AccountModal;
